import { equals } from "angular";
import { IIndicator, IUIError } from "@gtmhub/error-handling";
import { NameMap, toKeyMap } from "@gtmhub/util";
import { ICustomFieldsAllTypesMap } from "@webapp/custom-fields/models/custom-fields.models";
import { CustomFieldsAction, IResetIndicatorCustomFieldAction } from "./models";

export interface ICustomFieldsStoreState {
  customFields: ICustomFieldsState;
}

export interface ICustomFieldsState {
  isFetched: boolean;
  fetching?: IIndicator;
  creating?: IIndicator;
  updating?: IIndicator;
  deleting?: IIndicator;
  map: ICustomFieldsAllTypesMap;
  error: IUIError;
}

// You should pass currentArray as first array always and then the newArrays
function mergeKeyObjectArrays<A extends { [K in keyof A] }>(key: keyof A, ...arrays: A[][]): A[] {
  if (!arrays.length) {
    return [];
  }
  if (arrays.length === 1) {
    return arrays[0];
  }

  const maps = arrays.map((array) => toKeyMap(key, array));

  return Object.values(
    maps.slice(1).reduce((result, map) => {
      const merged: NameMap<A> = {};

      for (const key of Object.keys(map)) {
        if (key in result) {
          merged[key] = equals(result[key], map[key]) ? result[key] : map[key];
        } else {
          merged[key] = map[key];
        }
      }

      return merged;
    }, maps[0])
  );
}

const updateCustomFieldsMap = (partialMap: Partial<ICustomFieldsAllTypesMap>, map: ICustomFieldsAllTypesMap): ICustomFieldsAllTypesMap => {
  const cfTypeInPartialMap = Object.keys(partialMap)[0];

  return {
    ...map,
    [cfTypeInPartialMap]: mergeKeyObjectArrays("_id", map[cfTypeInPartialMap], partialMap[cfTypeInPartialMap]),
  };
};

export function customFieldsReducer(
  state: ICustomFieldsState = {
    isFetched: false,
    fetching: null,
    creating: null,
    updating: null,
    deleting: null,
    map: {} as ICustomFieldsAllTypesMap,
    error: null,
  },
  action: CustomFieldsAction
): ICustomFieldsState {
  switch (action.type) {
    case "REQUEST_CUSTOM_FIELDS":
      return { ...state, fetching: { progress: true } };
    case "RECEIVE_CUSTOM_FIELDS":
      return { ...state, isFetched: true, fetching: null, map: action.map };
    case "RECEIVE_CUSTOM_FIELDS_ERROR":
      return { ...state, fetching: { error: action.error } };

    case "UPDATE_CUSTOM_FIELDS":
      return { ...state, isFetched: true, map: updateCustomFieldsMap(action.map, state.map) };

    case "CREATE_CUSTOM_FIELD":
      return { ...state, creating: { progress: true } };
    case "CREATE_CUSTOM_FIELD_SUCCESS":
      return { ...state, creating: null };
    case "CREATE_CUSTOM_FIELD_ERROR":
      return { ...state, creating: { error: action.error } };

    case "UPDATE_CUSTOM_FIELD":
      return { ...state, updating: { progress: true } };
    case "UPDATE_CUSTOM_FIELD_SUCCESS":
      return { ...state, updating: null };
    case "UPDATE_CUSTOM_FIELD_ERROR":
      return { ...state, updating: { error: action.error } };

    case "DELETE_CUSTOM_FIELD":
      return { ...state, deleting: { progress: true } };
    case "DELETE_CUSTOM_FIELD_SUCCESS":
      return { ...state, deleting: null };
    case "DELETE_CUSTOM_FIELD_ERROR":
      return { ...state, deleting: { error: action.error } };

    case "RESET_INDICATOR": {
      const indicator = Object.keys(action).filter((key: keyof IResetIndicatorCustomFieldAction) => key !== "type")[0];
      return { ...state, [indicator]: null };
    }

    default: {
      return state;
    }
  }
}
